import { useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, Edit3, Plus, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { deleteArticle, getAdminArticles, getArticles } from '../api/articlesApi';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../hooks/useLanguage';

function getRoles(currentUser) {
  const rawRoles = currentUser?.roles || currentUser?.authorities || [];
  return rawRoles.map((role) => typeof role === 'string' ? role : role.authority || role.name || '');
}

function getArticleDateValue(article) {
  const raw = article.publishedAt || article.updatedAt || article.createdAt;
  const value = raw ? new Date(raw).getTime() : Number(article.id || 0);
  return Number.isNaN(value) ? Number(article.id || 0) : value;
}

function formatDate(value, lang) {
  if (!value) return null;
  try { return new Date(value).toLocaleDateString(lang === 'EN' ? 'en-GB' : 'ru-RU'); } catch { return null; }
}

const copy = {
  RU: {
    kicker: 'БИБЛИОТЕКА', title: 'Статьи и исследования', subtitle: 'Материалы об экземплярах, производстве, станках и отдельных деталях.',
    add: 'Добавить статью', empty: 'Статей пока нет.', loading: 'Загрузка библиотеки…', error: 'Не удалось загрузить статьи.',
    edit: 'Редактировать', remove: 'Удалить', views: 'Просмотров', published: 'Опубликовано', updated: 'Обновлено', draft: 'Черновик',
    noTitle: 'Без названия', confirm: 'Удалить статью без возможности восстановления?', deleteError: 'Не удалось удалить статью.', records: 'материалов', read: 'Читать',
  },
  EN: {
    kicker: 'LIBRARY', title: 'Articles & research', subtitle: 'Notes on surviving examples, production, mounts and individual parts.',
    add: 'Add article', empty: 'No articles yet.', loading: 'Loading the library…', error: 'Unable to load articles.',
    edit: 'Edit', remove: 'Delete', views: 'Views', published: 'Published', updated: 'Updated', draft: 'Draft',
    noTitle: 'Untitled', confirm: 'Permanently delete this article?', deleteError: 'Unable to delete the article.', records: 'records', read: 'Read',
  },
};

export default function ArticlesPage() {
  const { currentUser } = useAuth();
  const { lang } = useLanguage();
  const labels = copy[lang] || copy.RU;
  const roles = getRoles(currentUser);
  const isAdmin = roles.includes('ROLE_ADMIN') || roles.includes('ADMIN');
  const [articles, setArticles] = useState([]);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let active = true;
    setStatus('loading');
    const request = isAdmin ? getAdminArticles(lang) : getArticles(lang);
    request
      .then(({ data }) => {
        if (!active) return;
        setArticles(Array.isArray(data) ? data : []);
        setStatus('ready');
      })
      .catch(() => active && setStatus('error'));
    return () => { active = false; };
  }, [lang, isAdmin]);

  const sortedArticles = useMemo(() => [...articles].sort((a, b) => {
    const dateDiff = getArticleDateValue(b) - getArticleDateValue(a);
    return dateDiff || Number(b.id || 0) - Number(a.id || 0);
  }), [articles]);

  const handleDelete = async (articleId) => {
    if (!window.confirm(labels.confirm)) return;
    try {
      await deleteArticle(articleId);
      setArticles((current) => current.filter((article) => article.id !== articleId));
    } catch {
      window.alert(labels.deleteError);
    }
  };

  return (
    <main className="articles-page page-width">
      <header className="articles-header">
        <div>
          <p className="section-kicker">{labels.kicker}</p>
          <h1>{labels.title}</h1>
          <p>{labels.subtitle}</p>
        </div>
        <div className="articles-header__side">
          <span>{String(sortedArticles.length).padStart(2, '0')} {labels.records}</span>
          {isAdmin && <Link to="/admin/articles/new" className="secondary-button"><Plus size={16} />{labels.add}</Link>}
        </div>
      </header>

      {status === 'loading' && <div className="articles-state">{labels.loading}</div>}
      {status === 'error' && <div className="articles-state articles-state--error">{labels.error}</div>}
      {status === 'ready' && sortedArticles.length === 0 && <div className="articles-state">{labels.empty}</div>}

      {status === 'ready' && sortedArticles.length > 0 && (
        <div className="articles-list">
          {sortedArticles.map((article, index) => {
            const published = article.status === 'PUBLISHED';
            const date = formatDate(article.publishedAt || article.updatedAt || article.createdAt, lang);
            const uploadHost = process.env.NODE_ENV === 'development' ? 'http://localhost:8080' : '';
            const cover = article.coverPhotoUrl
              ? (article.coverPhotoUrl.startsWith('/') ? uploadHost + article.coverPhotoUrl : article.coverPhotoUrl)
              : null;
            const url = published ? '/articles/' + article.slug : '/admin/articles/' + article.id + '/edit';

            return (
              <article className="article-index-row" key={article.id}>
                <span className="article-index-row__number">{String(index + 1).padStart(2, '0')}</span>
                <Link to={url} className="article-index-row__cover" aria-label={article.title || labels.noTitle}>
                  {cover ? <img src={cover} alt="" /> : <span>М</span>}
                </Link>
                <div className="article-index-row__body">
                  <div className="article-index-row__meta">
                    {isAdmin && <span className={published ? 'is-published' : 'is-draft'}>{published ? labels.published : labels.draft}</span>}
                    {date && <span>{published ? labels.published : labels.updated}: {date}</span>}
                    <span>{labels.views}: {article.viewCount ?? 0}</span>
                  </div>
                  <h2><Link to={url}>{article.title || labels.noTitle}</Link></h2>
                  {article.summary && <p>{article.summary}</p>}
                  <div className="article-index-row__actions">
                    <Link to={url}>{labels.read}<ArrowUpRight size={15} /></Link>
                    {isAdmin && (
                      <>
                        <Link to={'/admin/articles/' + article.id + '/edit'}><Edit3 size={14} />{labels.edit}</Link>
                        <button type="button" onClick={() => handleDelete(article.id)}><Trash2 size={14} />{labels.remove}</button>
                      </>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </main>
  );
}
